import type { AxiosRequestConfig } from 'axios'

import { LIST_ITEMS_PER_PAGE } from 'common/constants'
import { callAPI, laborerAuthAPIWithCredentials } from 'api/axios'
import { GeneralFailResponse } from 'common/types/commonTypes'
import {
  LaborerAuthAppendixFromBE,
  LaborerAuthRequestFromBE,
  LaborerSalaryChangeRequestFromBE,
} from 'common/utils/convertResponse/convertLaborerAuthResponse'
import { ObjectWithTranslationFromBE } from 'common/types/transformedRespTypes'

export type GetAuthContractListParams = {
  perPage?: number
  page?: number
  status?: string
}

export type GetAuthContractListResponse = {
  data: Array<LaborerAuthRequestFromBE>
  meta: {
    last_page: number
    current_page: number
  }
}

const getAuthContractList = (
  { page = 0, perPage = LIST_ITEMS_PER_PAGE, status }: GetAuthContractListParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.get<GetAuthContractListResponse>('api/laborer/contracts', {
    params: {
      page: page + 1,
      per_page: perPage,
      status,
    },
    ...config,
  })

export type SalaryChangeRequestFromBE = LaborerSalaryChangeRequestFromBE

export type GetSalaryChangeRequestsResponse = {
  data: Array<SalaryChangeRequestFromBE>
}

const getSalaryChangeRequests = (params: void = undefined, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetSalaryChangeRequestsResponse>('api/laborer/salary-change-requests', {
    ...config,
  })

export type GetSalaryChangeRequestDetailsParams = {
  salaryChangeRequestId: number
}

export type GetSalaryChangeRequestDetailsResponse = {
  data: SalaryChangeRequestFromBE
}

const getSalaryChangeRequestDetails = (
  { salaryChangeRequestId }: GetSalaryChangeRequestDetailsParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.get<GetSalaryChangeRequestDetailsResponse>(
    `api/laborer/salary-change-requests/${salaryChangeRequestId}`,
    config,
  )

export const SALARY_CHANGE_REQUEST_REPLY = {
  REJECT: 'reject',
  APPROVE: 'approve',
} as const

export type SalaryChangeRequestReply = typeof SALARY_CHANGE_REQUEST_REPLY[keyof typeof SALARY_CHANGE_REQUEST_REPLY]

export type ReplySalaryChangeRequestParams = {
  salaryChangeRequestId: number
  reply: SalaryChangeRequestReply
}

export type ReplySalaryChangeRequestResponse = {}

const replySalaryChangeRequest = (
  { salaryChangeRequestId, reply }: ReplySalaryChangeRequestParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.post<ReplySalaryChangeRequestResponse>(
    `api/laborer/salary-change-requests/${salaryChangeRequestId}/${reply}`,
    config,
  )

export type GetAuthRequestDetailsParams = {
  requestId: number
}

export type GetAuthRequestDetailsResponse = {
  data: LaborerAuthRequestFromBE
}

export const getAuthRequestDetails = ({ requestId }: GetAuthRequestDetailsParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetAuthRequestDetailsResponse>(`api/laborer/contracts/${requestId}`, config)

export type RejectAuthRequestParams = {
  requestId: number
  rejectionReason: number
}

export type RejectAuthRequestResponse = {}

const rejectAuthRequest = ({ requestId, rejectionReason }: RejectAuthRequestParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<RejectAuthRequestResponse>(
    `api/laborer/contracts/${requestId}/reject`,
    {
      rejection_reason: rejectionReason,
    },
    config,
  )

export type GetRejectionReasonListParams = {}

export type GetRejectionReasonListResponse = Array<ObjectWithTranslationFromBE>

const getRejectionReasonList = (params: GetRejectionReasonListParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetRejectionReasonListResponse>('api/lookups/rejection-reasons', config)

export type AcceptAuthRequestParams = {
  requestId: number
}

export type AcceptAuthRequestResponse = {}

const acceptAuthRequest = ({ requestId }: AcceptAuthRequestParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<AcceptAuthRequestResponse>(`api/laborer/contracts/${requestId}/approve`, config)

export type GetAuthContractTextParams = {
  requestId: string
}

export type GetAuthContractTextResponse = {
  data: {
    id: number
    label_ar: string
    label_en: string
  }
}

const getAuthContractText = ({ requestId }: GetAuthContractTextParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetAuthContractTextResponse>(
    `api/laborer/contracts/${requestId}/legalText`,
    config,
  )

export type GetAuthAppendixListParams = {
  requestId: string
}

export type GetAuthAppendixListResponse = {
  data: Array<LaborerAuthAppendixFromBE>
}

const getAuthAppendixList = ({ requestId }: GetAuthAppendixListParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetAuthAppendixListResponse>(
    `api/laborer/contracts/${requestId}/appendixes/index`,
    config,
  )

export type GetAuthAppendixDetailsParams = {
  requestId: string
  appendixId: string
}

export type GetAuthAppendixDetailsResponse = {
  data: LaborerAuthAppendixFromBE
}

const getAuthAppendixDetails = (
  { requestId, appendixId }: GetAuthAppendixDetailsParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.get<GetAuthAppendixDetailsResponse>(
    `api/laborer/contracts/${requestId}/appendixes/${appendixId}`,
    config,
  )

export type ApproveAuthAppendixParams = {
  requestId: number
  appendixId: number
}

export type ApproveAuthAppendixResponse = {}

const approveAuthAppendix = ({ requestId, appendixId }: ApproveAuthAppendixParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<ApproveAuthAppendixResponse>(
    `api/laborer/contracts/${requestId}/appendixes/${appendixId}/approve`,
    config,
  )

export type RejectAuthAppendixParams = {
  requestId: number
  appendixId: number
  rejectionReason: number
}

export type RejectAuthAppendixResponse = {}

const rejectAuthAppendix = (
  { requestId, appendixId, rejectionReason }: RejectAuthAppendixParams,
  config?: AxiosRequestConfig,
) =>
  laborerAuthAPIWithCredentials.post<RejectAuthAppendixResponse>(
    `api/laborer/contracts/${requestId}/appendixes/${appendixId}/reject`,
    {
      rejection_reason: rejectionReason,
    },
    config,
  )

export type GetAuthContractPdfParams = {
  requestId: string
  fileId: string
}

export type GetAuthContractPdfResponse = {
  url: string
}

const getAuthContractPdf = ({ requestId, fileId }: GetAuthContractPdfParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetAuthContractPdfResponse>(
    `api/laborer/contracts/${requestId}/pdf/${fileId}`,
    config,
  )

export type GetPendingAppendixParams = {
  requestId: string
  appendixId: string
}

export type GetPendingAppendixResponse = {
  legalTextAr: string
  legalTextEn: string
}

const getPendingAppendix = ({ requestId, appendixId }: GetPendingAppendixParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetPendingAppendixResponse>(
    `api/laborer/contracts/${requestId}/appendixes/${appendixId}/draft`,
    config,
  )

export type CheckLaborerSurveyEligibilityResponse = {
  eligible: boolean
  rating_expiration: string | null
}

const checkLaborerSurveyEligibility = (params = {}, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<CheckLaborerSurveyEligibilityResponse>('api/laborer/rating/eligible', config)

export type GetLaborerSurveyQuestionsResponse = {
  data: Array<{ id: number; key: string }>
}

const getLaborerSurveyQuestions = (params = {}, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetLaborerSurveyQuestionsResponse>('api/laborer/rating/questions', config)

export type GetLaborerSubmitedRatingsResponse = {
  data: Array<{
    id: number
    question_id: number
    employer_name: string
    period_start: string
    period_end: string
    submission_date: string
    star_rating_value: number
  }>
}

const getLaborerSubmitedRatings = (params = {}, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<GetLaborerSubmitedRatingsResponse>('api/laborer/rating', config)

export type SubmitLaborerRatingParams = {
  answers: Record<string, number>
}

export type SubmitLaborerRatingResponse = {}

const submitLaborerRating = (params: SubmitLaborerRatingParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<SubmitLaborerRatingResponse>('api/laborer/rating', params, config)

export type CheckLaborerProSurveyEligibilityResponse = {
  eligible: boolean
}

const checkLaborerProSurveyEligibility = (params = {}, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.get<CheckLaborerProSurveyEligibilityResponse>(
    'api/laborer/rating/pro/eligible',
    config,
  )

export type SubmitLaborerProSurveyParams = {
  answers: Record<string, number>
}

export type SubmitLaborerProSurveyResponse = {}

const submitLaborerProSurvey = (params: SubmitLaborerProSurveyParams, config?: AxiosRequestConfig) =>
  laborerAuthAPIWithCredentials.post<SubmitLaborerProSurveyResponse>('api/laborer/rating/pro', params, config)

export default {
  getAuthContractList: callAPI<typeof getAuthContractList, GetAuthContractListResponse, GeneralFailResponse>(
    getAuthContractList,
  ),
  getSalaryChangeRequests: callAPI<
    typeof getSalaryChangeRequests,
    GetSalaryChangeRequestsResponse,
    GeneralFailResponse
  >(getSalaryChangeRequests),
  getSalaryChangeRequestDetails: callAPI<
    typeof getSalaryChangeRequestDetails,
    GetSalaryChangeRequestDetailsResponse,
    GeneralFailResponse
  >(getSalaryChangeRequestDetails),
  replySalaryChangeRequest: callAPI<
    typeof replySalaryChangeRequest,
    ReplySalaryChangeRequestResponse,
    GeneralFailResponse
  >(replySalaryChangeRequest),
  getAuthRequestDetails: callAPI<typeof getAuthRequestDetails, GetAuthRequestDetailsResponse, GeneralFailResponse>(
    getAuthRequestDetails,
  ),
  getAuthContractText: callAPI<typeof getAuthContractText, GetAuthContractTextResponse, GeneralFailResponse>(
    getAuthContractText,
  ),
  rejectAuthRequest: callAPI<typeof rejectAuthRequest, RejectAuthRequestResponse, GeneralFailResponse>(
    rejectAuthRequest,
  ),
  acceptAuthRequest: callAPI<typeof acceptAuthRequest, AcceptAuthRequestResponse, GeneralFailResponse>(
    acceptAuthRequest,
  ),
  getRejectionReasonList: callAPI<typeof getRejectionReasonList, GetRejectionReasonListResponse, GeneralFailResponse>(
    getRejectionReasonList,
  ),
  getAuthAppendixList: callAPI<typeof getAuthAppendixList, GetAuthAppendixListResponse, GeneralFailResponse>(
    getAuthAppendixList,
  ),
  getAuthAppendixDetails: callAPI<typeof getAuthAppendixDetails, GetAuthAppendixDetailsResponse, GeneralFailResponse>(
    getAuthAppendixDetails,
  ),
  approveAuthAppendix: callAPI<typeof approveAuthAppendix, ApproveAuthAppendixResponse, GeneralFailResponse>(
    approveAuthAppendix,
  ),
  rejectAuthAppendix: callAPI<typeof rejectAuthAppendix, RejectAuthAppendixResponse, GeneralFailResponse>(
    rejectAuthAppendix,
  ),
  getAuthContractPdf: callAPI<typeof getAuthContractPdf, GetAuthContractPdfResponse, GeneralFailResponse>(
    getAuthContractPdf,
  ),
  getPendingAppendix: callAPI<typeof getPendingAppendix, GetPendingAppendixResponse, GeneralFailResponse>(
    getPendingAppendix,
  ),
  checkLaborerSurveyEligibility: callAPI<
    typeof checkLaborerSurveyEligibility,
    CheckLaborerSurveyEligibilityResponse,
    GeneralFailResponse
  >(checkLaborerSurveyEligibility),
  getLaborerSurveyQuestions: callAPI<
    typeof getLaborerSurveyQuestions,
    GetLaborerSurveyQuestionsResponse,
    GeneralFailResponse
  >(getLaborerSurveyQuestions),
  getLaborerSubmitedRatings: callAPI<
    typeof getLaborerSubmitedRatings,
    GetLaborerSubmitedRatingsResponse,
    GeneralFailResponse
  >(getLaborerSubmitedRatings),
  submitLaborerRating: callAPI<typeof submitLaborerRating, SubmitLaborerRatingResponse, GeneralFailResponse>(
    submitLaborerRating,
  ),
  checkLaborerProSurveyEligibility: callAPI<
    typeof checkLaborerProSurveyEligibility,
    CheckLaborerProSurveyEligibilityResponse,
    GeneralFailResponse
  >(checkLaborerProSurveyEligibility),
  submitLaborerProSurvey: callAPI<typeof submitLaborerProSurvey, SubmitLaborerProSurveyResponse, GeneralFailResponse>(
    submitLaborerProSurvey,
  ),
}
